/* eslint-disable max-len */
import {
    Box, Button, Grid, Heading, HStack, Image, Link, Skeleton, Text, VStack,
} from '@chakra-ui/react';
import {
    FaIcon,
    Hide, LineBreak, Show,
} from '@acp/core-ui';
import { mobileApps } from '@acp/core-ui/assets';
import { faCheckCircle, IconDefinition } from '@fortawesome/sharp-light-svg-icons';
import { CardLayout } from '../dashboard';

function FeatureTextItem({
    icon = faCheckCircle,
    title,
    description,
}: {
    icon?: IconDefinition;
    title: string;
    description: string;
}) {
    return (
        <Box flex={1}>
            <HStack alignItems="flex-start" gap={4}>
                {icon !== undefined && <FaIcon icon={icon} color="primary.500" h={6} w={6} />}

                <VStack alignItems="flex-start" gap={2}>
                    <Text fontWeight="bold" color="white" fontSize="md">
                        {title}
                    </Text>
                    <Text color="white" fontSize="sm" lineHeight={1.5} maxW="md">
                        {description}
                    </Text>
                </VStack>
            </HStack>
        </Box>
    );
}

function MinimumStandardsMarketingSkeleton() {
    return (
        <VStack gap={0} w="full">
            <HStack bg="white" w="full" minH={64}>
                <CardLayout
                    flexDir={['column-reverse', null, 'row']}
                    alignItems="center"
                    justifyContent="space-between"
                    gap={8}
                    px={5}
                >
                    <VStack
                        w={{ base: 'full', lg: '60%' }}
                        alignItems={{ base: 'center', md: 'flex-start' }}
                        justifyContent={{ base: 'center', md: 'flex-start' }}
                    >
                        <Skeleton height={12} width="85%" />
                        <Skeleton height={8} width="70%" />
                        <Skeleton height={10} width={32} my={4} />
                        <HStack gap={2}>
                            <Skeleton height={8} width={32} />
                            <Skeleton height={8} width={32} />
                        </HStack>
                    </VStack>
                    <Box w={{ base: 'full', lg: '40%' }} display="flex" justifyContent={{ base: 'center', lg: 'flex-start' }}>
                        <Skeleton height={300} width={250} />
                    </Box>
                </CardLayout>
            </HStack>

            <VStack bg="gray.100" w="full" py={[8, null, 16]} gap={8}>
                <CardLayout flexDir="column" alignItems="stretch" px={5} mt={0}>
                    <Skeleton height={6} width={64} />
                    <Box
                        display="grid"
                        gridTemplateColumns={['1fr', null, '1fr 1fr']}
                        gap={[6, null, 8]}
                    >
                        {[1, 2, 3, 4].map((i) => (
                            <HStack key={i} alignItems="flex-start" gap={4}>
                                <Skeleton boxSize={6} flexShrink={0} />
                                <VStack alignItems="flex-start" gap={2} flex={1}>
                                    <Skeleton height={4} width="80%" />
                                    <Skeleton height={12} width="100%" />
                                </VStack>
                            </HStack>
                        ))}
                    </Box>
                </CardLayout>
            </VStack>

            <VStack w="full" py={[8, null, 12]}>
                <CardLayout flexDir="column" alignItems="flex-start" px={{ base: 5, lg: 0 }}>
                    <VStack alignItems={['center', null, 'flex-start']} w="full" p={6}>
                        <Skeleton height={10} width="60%" />
                        <Skeleton height={6} width="40%" />
                        <Skeleton height={10} width={40} mt={4} />
                    </VStack>
                </CardLayout>
            </VStack>
        </VStack>
    );
}
export function MinimumStandardsMarketingPage() {
    return (
        <VStack gap={0}>
            {/* hero section */}
            <HStack bg="white" w="full" h="full">
                <CardLayout
                    flexDir={['column-reverse', null, 'row']}
                    alignItems="center"
                    justifyContent="space-between"
                    gap={8}
                    px={5}
                >
                    <VStack w={{ base: 'full', lg: '60%' }} alignItems="start" gap={6}>
                        <Heading fontSize={['2xl', null, '2xl', '4xl']} textAlign={['center', null, 'left']}>
                            This page would be much more interesting
                            {' '}
                            <LineBreak />
                            if you were connected to minimum
                            {' '}
                            <LineBreak />
                            standards assessments
                        </Heading>

                        <VStack w="full" alignItems={['center', null, 'start']}>
                            <Text fontSize="small" color="muted.500">
                                Powered by
                            </Text>
                            <Image src={mobileApps.properlyLogoBlackText} alt="Minimum standards" h={10} />
                        </VStack>

                        <VStack w="full" alignItems={['center', null, 'start']} mt={8} gap={4}>
                            <Text fontSize="md">
                                Download the app to start the demo
                            </Text>
                            <HStack gap={4}>
                                <Link href="https://apps.apple.com/au/app/properly/id6748495141" isExternal>
                                    <Image src={mobileApps.downloadAppStore} alt="Properly on the App Store" h={10} />
                                </Link>
                                <Link href="https://play.google.com/store/apps/details?id=au.com.detectorinspector.properly" isExternal>
                                    <Image src={mobileApps.downloadGooglePlay} alt="Properly on Google Play" h={10} />
                                </Link>
                            </HStack>
                        </VStack>
                    </VStack>
                    <Box
                        w={{ base: 'full', lg: '40%' }}
                        display="flex"
                        justifyContent={{ base: 'center', lg: 'flex-start' }}
                    >
                        <Show above="lg">
                            <Image src={mobileApps.properlyAppImgDesktop} alt="Properly app" maxW={[300, null, 400]} h="auto" />
                        </Show>
                        <Hide above="lg">
                            <Image src={mobileApps.properlyAppImgMobile} alt="Properly app" maxW={[240, null, 300]} h="auto" />
                        </Hide>
                    </Box>
                </CardLayout>
            </HStack>

            {/* feature section */}
            <VStack bg="secondary.500" w="full" textColor="white" py={[8, null, 16]} gap={8}>
                <CardLayout flexDir="column" alignItems="stretch" px={5} mt={0}>
                    <Heading
                        size="md"
                        color="white"
                        fontWeight="semibold"
                    >
                        What&apos;s different about Properly?
                    </Heading>

                    <Grid
                        templateColumns={['1fr', null, '1fr 1fr']}
                        gap={[6, null, 8]}
                    >
                        <FeatureTextItem
                            title="You capture, we assess"
                            description="Your agency staff don't need to be amateur minimum standard auditors.
                            Our workflows ensure that the video footage we need is captured. Our assessors will do the analysis."
                        />
                        <FeatureTextItem
                            title="No access delay"
                            description="Your agency staff are already in the property prior to advertising.
                            Avoid delays in coordinating third parties and use Properly to rapidly capture the video footage we need to complete the assessment."
                        />
                        <FeatureTextItem
                            title="Guarantee for your agency"
                            description="DI stands behind its assessments, with guarantees to put your mind at ease.
                            Your team don't need to take the risk. We will."
                        />
                        <FeatureTextItem
                            title="1 business day turnaround"
                            description="Once submitted through Properly, DI's assessment team reviews your footage
                            and returns the findings within 1 business day."
                        />
                    </Grid>
                </CardLayout>
            </VStack>

            {/* how it works section */}
            <VStack w="full" py={[8, null, 12]}>
                <CardLayout flexDir="column" px={5} gap={8}>
                    <VStack alignItems={['center', null, 'start']} gap={6} maxW="3xl">
                        <Heading size="lg" textAlign={['center', null, 'left']}>
                            Avoid delays in advertising your properties.
                            {' '}
                            <LineBreak />
                            Get minimum standards assessments done Properly
                            {' '}
                            <LineBreak />
                            by Detector Inspector.
                        </Heading>
                        <Text fontSize="md" color="charcoal.400" textAlign={['center', null, 'left']}>
                            Click below to request trial access and your account manager
                            {' '}
                            <LineBreak />
                            will be in touch to take you through the details.
                        </Text>

                        <Button
                            as={Link}
                            href="https://www.detectorinspector.com.au/services/minimum-standards-assessments/#howitworks"
                            isExternal
                            variant="rounded-solid"
                            colorScheme="secondary"
                            size={['cta-sm', null, 'cta-md']}
                            textDecoration="none"
                            mt={[4, null, 8]}
                            _hover={{
                                textDecoration: 'none',
                                bg: 'white',
                                color: 'secondary.500',
                            }}
                        >
                            See how it works
                        </Button>
                    </VStack>
                </CardLayout>
            </VStack>
        </VStack>
    );
}

MinimumStandardsMarketingPage.Skeleton = MinimumStandardsMarketingSkeleton;
